import { z } from "zod";

/**
 * Opaque identifier for any record (session, claim, overlap, message, event, deploy).
 * Treat it as a black box: never parse it. The mock coordinator uses a
 * readable `<prefix>_<n>` form (for example `ses_0001`), a real coordinator may use ULIDs.
 */
export const IdSchema = z.string().min(1).max(128);
/** See {@link IdSchema}. */
export type Id = z.infer<typeof IdSchema>;

/**
 * An instant in time as an ISO 8601 UTC string with a `Z` suffix,
 * for example `2026-10-01T09:30:00.000Z`. All timestamps in Botallion use this format.
 */
export const TimestampSchema = z.iso.datetime();
/** See {@link TimestampSchema}. */
export type Timestamp = z.infer<typeof TimestampSchema>;

/**
 * Work package identifier: `WP-` followed by two or three digits, for example `WP-00` or `WP-104`.
 * Every session, claim and verdict is tied to exactly one work package.
 */
export const WpIdSchema = z.string().regex(/^WP-\d{2,3}$/, "expected WP-xx");
/** See {@link WpIdSchema}. */
export type WpId = z.infer<typeof WpIdSchema>;

/**
 * A git object id: lowercase hex, 40 characters (SHA-1) or 64 characters (SHA-256).
 * The all-zero id means "no commit" (for example `before` on a newly created ref).
 */
export const CommitShaSchema = z
  .string()
  .regex(/^(?:[0-9a-f]{40}|[0-9a-f]{64})$/, "expected a 40 or 64 character lowercase hex git sha");
/** See {@link CommitShaSchema}. */
export type CommitSha = z.infer<typeof CommitShaSchema>;

/** The all-zero SHA-1 git uses to mean "this ref did not exist". */
export const ZERO_SHA = "0000000000000000000000000000000000000000";

/**
 * A repo-relative path glob, POSIX separators, no leading `./` or `/`.
 * Supported syntax: `*` (any run of characters within one segment), `**` (zero or more
 * whole segments), `?` (one character), `{a,b}` (alternatives). Everything else is literal.
 * Examples: `apps/api/src/**`, `apps/api/migrations/*.sql`, `packages/ui/src/{Button,Card}.tsx`.
 */
export const PathGlobSchema = z
  .string()
  .min(1)
  .refine((s) => !s.startsWith("/") && !s.startsWith("./"), "path globs are repo-relative: no leading / or ./")
  .refine((s) => !s.includes("\\"), "use / as the path separator");
/** See {@link PathGlobSchema}. */
export type PathGlob = z.infer<typeof PathGlobSchema>;

/**
 * A code symbol an agent intends to change, written `<file path>#<qualified name>`,
 * for example `demo/api/src/lib/ladder.ts#applyResult` or `demo/api/src/db.ts#MatchesRepo.insert`.
 * Overlap detection compares symbols by exact string match, so name the most specific symbol you will edit.
 */
export const SymbolRefSchema = z
  .string()
  .regex(/^[^#\s]+#[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*)*$/, "expected <path>#<Name> or <path>#<Name.member>");
/** See {@link SymbolRefSchema}. */
export type SymbolRef = z.infer<typeof SymbolRefSchema>;

/**
 * A named interface between work packages, written `<kind>:<identifier>`.
 * Conventional kinds:
 * - `api:<METHOD> <path>` an HTTP route, for example `api:POST /tips` or `api:GET /ladder`
 * - `schema:<table>` a D1 table or shared data shape, for example `schema:tips`
 * - `binding:<NAME>` a Worker binding, for example `binding:API_SERVICE`
 * - `module:<path>` a shared module other packages import, for example `module:demo/api/src/lib/validation.ts`
 * - `event:<name>`, `queue:<name>`, `env:<NAME>` for events, queues and environment variables
 *
 * Overlap detection compares contract refs by exact string match. Write them exactly as above:
 * lowercase kind, uppercase HTTP method, one space, path params as `:name`.
 */
export const ContractRefSchema = z
  .string()
  .regex(/^[A-Za-z][A-Za-z0-9_-]*:\S.*$/, "expected <kind>:<identifier>, for example api:POST /tips");
/** See {@link ContractRefSchema}. */
export type ContractRef = z.infer<typeof ContractRefSchema>;

/**
 * What a claim or work package intends to touch. Shared by {@link IntentClaim} and {@link WorkPackage}.
 * All four fields are required; use empty arrays and `false` when a dimension does not apply
 * (or build one with `makeScope`).
 */
export const ScopeSchema = z.object({
  /** Repo-relative path globs the work will edit. */
  paths: z.array(PathGlobSchema),
  /** Specific symbols the work will edit, see {@link SymbolRefSchema}. */
  symbols: z.array(SymbolRefSchema),
  /** Cross-package interfaces the work will create or change, see {@link ContractRefSchema}. */
  contracts: z.array(ContractRefSchema),
  /**
   * True when the work adds or edits a database migration. Migration numbering is global,
   * so any two concurrent claims with this flag set always collide.
   */
  migration: z.boolean(),
});
/** See {@link ScopeSchema}. */
export type Scope = z.infer<typeof ScopeSchema>;

/** Build a {@link Scope}, defaulting every omitted field to empty / false. */
export function makeScope(partial: Partial<Scope> = {}): Scope {
  return {
    paths: partial.paths ?? [],
    symbols: partial.symbols ?? [],
    contracts: partial.contracts ?? [],
    migration: partial.migration ?? false,
  };
}

/**
 * Who did or agreed to something. One of:
 * - a session id (an agent), for example `ses_0001`
 * - a human, written `human:<handle>`, for example `human:ryan`. Humans approve plans and promotions and break deadlocks.
 *   Since contracts-v2 the handle is the signed-in account's id (`human:acc_...`, see `accountPrincipal`).
 * - a platform component, written `system:<name>`, for example `system:coordinator`, `system:verifier`, `system:planner`.
 */
export const PrincipalSchema = z.string().min(1).max(128);
/** See {@link PrincipalSchema}. */
export type Principal = z.infer<typeof PrincipalSchema>;

/** The principal the coordinator uses for messages and rule-based resolutions it creates itself. */
export const COORDINATOR_PRINCIPAL = "system:coordinator";

/** True when a principal is a human (`human:<handle>`). */
export function isHumanPrincipal(principal: string): boolean {
  return /^human:\S+$/.test(principal);
}

/** True when a principal is a platform component (`system:<name>`). */
export function isSystemPrincipal(principal: string): boolean {
  return /^system:\S+$/.test(principal);
}
